import React, { useState } from "react";
import { Box, Chip, Link, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from "@mui/material";
import ImageNotSupportedOutlinedIcon from "@mui/icons-material/ImageNotSupportedOutlined";

const DAY = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" });
const STAMP = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
const day = (v) => (v ? DAY.format(new Date(v)) : null);
const stamp = (v) => (v ? STAMP.format(new Date(v)) : null);
const pct = (v) => (typeof v === "number" ? `${Math.round(v * 100)}%` : null);
const cap = (s) => (s ? s[0].toUpperCase() + s.slice(1) : s);

// An image that says so when there is none (an empty cell has no crop, and a
// capture is often imaged on one side only), or when it would not load.
const Picture = ({ src, alt, side, unavailable }) => {
  const [broken, setBroken] = useState(false);
  if (!src || broken) {
    return (
      <Box sx={{ height: 280, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", color: "text.disabled", gap: 1 }}>
        <ImageNotSupportedOutlinedIcon />
        <Typography variant="body2">{src || unavailable ? `The ${side} image could not be loaded` : `No ${side} image`}</Typography>
      </Box>
    );
  }
  return (
    <Box component="img" src={src} alt={alt} onError={() => setBroken(true)} sx={{ display: "block", width: "100%", height: 280, objectFit: "contain", bgcolor: "grey.100" }} />
  );
};

// A link to one of the tray's images, or, when it has none on that side, the
// same words greyed out with the reason: never a link to a missing file.
const TrayImageLink = ({ src, side, unavailable }) => {
  const label = `Tray image (${side})`;
  if (src) {
    return (
      <Link href={src} target="_blank" rel="noopener" variant="body2">
        {label}
      </Link>
    );
  }
  return (
    <Tooltip title={unavailable ? `The ${side} tray image could not be loaded. Try again later.` : `This tray has no ${side} image`}>
      <Typography variant="body2" color="text.disabled" tabIndex={0} aria-disabled="true" sx={{ cursor: "default" }}>
        {label}
      </Typography>
    </Tooltip>
  );
};

const Row = ({ label, children }) =>
  children == null || children === "" ? null : (
    <Box component="div" sx={{ display: "grid", gridTemplateColumns: "150px 1fr", gap: 1, py: 0.5, borderBottom: 1, borderColor: "divider" }}>
      <Typography component="dt" variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography component="dd" variant="body2" sx={{ m: 0 }}>
        {children}
      </Typography>
    </Box>
  );

const Section = ({ title, children }) => (
  <Box component="section" sx={{ mt: 2 }}>
    <Typography variant="overline" color="text.secondary" component="h3" sx={{ lineHeight: 2 }}>
      {title}
    </Typography>
    <Box component="dl" sx={{ m: 0 }}>
      {children}
    </Box>
  </Box>
);

const sideResult = (side) => {
  if (!side) return null;
  if (side.empty && side.empty !== "obj") return cap(side.empty);
  const name = [cap(side.genus), side.species].filter(Boolean).join(" ");
  return [name || "No result", side.sex && cap(side.sex), pct(side.speciesConfidence)].filter(Boolean).join(" · ");
};

/** One specimen: its images, its results and where it came from. */
export const SpecimenView = ({ data, children }) => {
  const { tray, specimen, images } = data;
  // The side the user chose, kept while stepping through specimens. A side
  // with no image, when the other has one, is disabled and shows the other;
  // a side whose image could not be loaded stays selectable to say so.
  const [chosen, setSide] = useState("front");
  const unavailable = images?.unavailable ?? [];
  const opposite = (s) => (s === "front" ? "back" : "front");
  const onlyOther = (s) => !images?.[s] && !unavailable.includes(s) && !!images?.[opposite(s)];
  const side = onlyOther(chosen) ? opposite(chosen) : chosen;
  const { final, algorithm, contributed } = specimen;
  return (
    <Box>
      <Box sx={{ position: "relative", border: 1, borderColor: "divider", borderRadius: 1, overflow: "hidden" }}>
        <Picture
          key={`${specimen.id}-${side}`}
          src={images?.[side]}
          side={side}
          unavailable={unavailable.includes(side)}
          alt={`Specimen ${specimen.position + 1}, ${side}`}
        />
        <ToggleButtonGroup
          exclusive
          size="small"
          value={side}
          onChange={(_, v) => v && setSide(v)}
          aria-label="Image side"
          sx={{ position: "absolute", top: 8, left: 8, bgcolor: "background.paper" }}
        >
          <ToggleButton value="front" disabled={onlyOther("front")} sx={{ px: 1.25, py: 0.25, textTransform: "none" }}>
            Front
          </ToggleButton>
          <ToggleButton value="back" disabled={onlyOther("back")} sx={{ px: 1.25, py: 0.25, textTransform: "none" }}>
            Back
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>
      <Box sx={{ display: "flex", gap: 2, mt: 1 }}>
        <TrayImageLink src={images?.trayFront} side="front" unavailable={unavailable.includes("trayFront")} />
        <TrayImageLink src={images?.trayBack} side="back" unavailable={unavailable.includes("trayBack")} />
      </Box>

      <Box sx={{ mt: 2, display: "flex", alignItems: "baseline", gap: 1, flexWrap: "wrap" }}>
        <Typography variant="h6" component="p" sx={{ fontStyle: final.category === "species" ? "italic" : "normal" }}>
          {final.species}
        </Typography>
        {final.sex && <Chip size="small" label={final.sex} />}
        <Chip size="small" variant="outlined" label={final.source === "contributed" ? "Contributed" : "Algorithm"} />
      </Box>

      {children}

      <Section title="Algorithm">
        <Row label="Result">{algorithm.result}</Row>
        <Row label="Before threshold">{algorithm.beforeThreshold !== algorithm.result ? algorithm.beforeThreshold : null}</Row>
        <Row label="Unknown because">{algorithm.unknownReason}</Row>
        <Row label="Front">{sideResult(algorithm.front)}</Row>
        <Row label="Back">{sideResult(algorithm.back)}</Row>
        <Row label="Version">{[tray.algorithmVersion, tray.region].filter(Boolean).join(" · ")}</Row>
        <Row label="Confidence threshold">{tray.confidenceThreshold != null ? `${tray.confidenceThreshold}%` : null}</Row>
      </Section>

      {contributed && (contributed.genus || contributed.sex || contributed.lifeStage || contributed.gonotrophic) && (
        <Section title="Contributed">
          <Row label="Identification">{[contributed.genus, contributed.species].filter(Boolean).join(" ") || null}</Row>
          <Row label="Sex">{cap(contributed.sex)}</Row>
          <Row label="Life stage">{contributed.lifeStage}</Row>
          <Row label="Gonotrophic state">{contributed.gonotrophic}</Row>
          <Row label="Last changed">
            {[stamp(contributed.updatedOn), contributed.updatedBy && `by ${contributed.updatedBy}`].filter(Boolean).join(" ") || null}
          </Row>
        </Section>
      )}

      <Section title="Collection">
        <Row label="Collection site">
          {tray.collectionSite ? [tray.collectionSite.name, tray.collectionSite.county].filter(Boolean).join(" · ") : "No collection site"}
        </Row>
        <Row label="Collection method">{tray.collectionMethod}</Row>
        <Row label="Collection date">{day(tray.collectionDate) ?? "Not entered"}</Row>
        <Row label="Imaged">{stamp(tray.imagedAt)}</Row>
        <Row label="Device">{tray.device}</Row>
        <Row label="User">{tray.user}</Row>
        <Row label="Tray">{`${tray.id}, position ${specimen.position + 1}`}</Row>
        <Row label="Tray tags">{tray.tags?.length ? tray.tags.join(", ") : null}</Row>
        <Row label="Specimen tags">{specimen.tags?.length ? specimen.tags.join(", ") : null}</Row>
        <Row label="Tray notes">{tray.notes}</Row>
      </Section>
    </Box>
  );
};
